/**
 * Loumars.Theme — Early Theme Mode Initialization & Mobile Switch Controller Script
 * Runs in <head> to eliminate Flash of Unstyled Theme (FOUT) and provide instantaneous mobile-style Day/Night switch controls.
 * Strictly standalone per Standalone Assets Policy.
 */
(function (): void {
  'use strict';
  const STORAGE_KEY = 'loumars-theme-mode';
  let isToggling = false;

  function getStoredMode(): 'light' | 'dark' | 'system' {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'dark' || stored === 'light' || stored === 'system') return stored;
    } catch {}
    return 'system';
  }

  function updateSwitchUI(isDark: boolean): void {
    const title = isDark ? 'Switch to Day Mode' : 'Switch to Night Mode';
    const iconName = isDark ? 'dark_mode' : 'light_mode';
    const toggles = document.querySelectorAll<HTMLElement>('[data-theme-toggle]');
    for (let i = 0; i < toggles.length; i++) {
      const btn = toggles[i];
      btn.setAttribute('role', 'switch');
      btn.setAttribute('aria-checked', isDark ? 'true' : 'false');
      btn.setAttribute('title', title);
      btn.setAttribute('aria-label', title);
      const iconEl = btn.querySelector<HTMLElement>('[data-theme-icon]');
      if (iconEl) iconEl.textContent = iconName;
      const thumb = btn.querySelector<HTMLElement>('[data-theme-switch-thumb]');
      if (thumb) {
        if (isDark) {
          thumb.classList.remove('translate-x-0');
          thumb.classList.add('translate-x-6');
        } else {
          thumb.classList.remove('translate-x-6');
          thumb.classList.add('translate-x-0');
        }
      }
    }
  }

  function applyMode(mode: 'light' | 'dark' | 'system', isInteractive = false): void {
    const root = document.documentElement;
    const currentThemeMode = root.getAttribute('data-theme-mode');

    let isDark = false;
    if (mode === 'dark') {
      isDark = true;
    } else if (mode === 'light') {
      isDark = false;
    } else {
      isDark = Boolean(
        window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
      );
    }

    const targetClass = isDark ? 'dark' : 'light';
    const removeClass = isDark ? 'light' : 'dark';

    const body = document.body;

    if (
      currentThemeMode === mode &&
      root.classList.contains(targetClass) &&
      !root.classList.contains(removeClass) &&
      (!body || (body.classList.contains(targetClass) && !body.classList.contains(removeClass)))
    ) {
      updateSwitchUI(isDark);
      return;
    }

    // Suppress CSS transitions ONLY during interactive mode switches to prevent transition stutter,
    // but exempt the theme toggle switch so the iOS thumb and track animate smoothly.
    let disabler: HTMLStyleElement | null = null;
    if (isInteractive && typeof document !== 'undefined' && document.head) {
      disabler = document.createElement('style');
      disabler.setAttribute('data-theme-transition-guard', 'true');
      disabler.textContent =
        '*:not([data-theme-toggle]):not([data-theme-toggle] *):not([data-theme-switch]):not([data-theme-switch] *):not([data-theme-switch-thumb]):not([data-theme-switch-thumb] *):not([data-theme-icon]):not(.ios-switch-track):not(.ios-switch-track *):not(.ios-switch-thumb):not(.ios-switch-thumb *) { -webkit-transition: none !important; -moz-transition: none !important; -o-transition: none !important; -ms-transition: none !important; transition: none !important; }';
      document.head.appendChild(disabler);
    }

    if (isDark) {
      root.classList.add('dark');
      root.classList.remove('light');
      if (body) {
        body.classList.add('dark');
        body.classList.remove('light');
      }
    } else {
      root.classList.add('light');
      root.classList.remove('dark');
      if (body) {
        body.classList.add('light');
        body.classList.remove('dark');
      }
    }
    root.setAttribute('data-theme-mode', mode);
    if (body) {
      body.setAttribute('data-theme-mode', mode);
      // Force layout reflow ONLY during interactive user toggles
      if (isInteractive) {
        void body.offsetHeight;
      }
    }

    if (disabler) {
      if (window.requestAnimationFrame) {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            if (disabler && disabler.parentNode) {
              disabler.parentNode.removeChild(disabler);
            }
          });
        });
      } else {
        setTimeout(() => {
          if (disabler && disabler.parentNode) {
            disabler.parentNode.removeChild(disabler);
          }
        }, 50);
      }
    }

    try {
      if (localStorage.getItem(STORAGE_KEY) !== mode) {
        localStorage.setItem(STORAGE_KEY, mode);
      }
    } catch {}

    updateSwitchUI(isDark);

    try {
      window.dispatchEvent(
        new CustomEvent('loumars-theme-mode-changed', { detail: { mode, isDark } })
      );
    } catch {}
  }

  function toggleMode(): 'light' | 'dark' | 'system' {
    const isDark = document.documentElement.classList.contains('dark');
    const next: 'light' | 'dark' = isDark ? 'light' : 'dark';
    applyMode(next, true);
    return next;
  }

  function cycleMode(): 'light' | 'dark' | 'system' {
    return toggleMode();
  }

  function handleToggleClick(e: Event): void {
    if (e) {
      if (e.preventDefault) e.preventDefault();
      if (e.stopPropagation) e.stopPropagation();
    }
    if (isToggling) return;
    isToggling = true;
    setTimeout(function () {
      isToggling = false;
    }, 150);
    toggleMode();
  }

  // Early execution in <head> — zero layout reflow
  applyMode(getStoredMode(), false);

  // Expose global methods
  window.afGetThemeMode = getStoredMode;
  window.afSetThemeMode = (mode: 'light' | 'dark' | 'system') => applyMode(mode, true);
  window.afToggleThemeMode = toggleMode;
  window.afCycleThemeMode = cycleMode;
  window.getImmersiveThemeMode = getStoredMode;
  window.setImmersiveThemeMode = (mode: 'light' | 'dark' | 'system') => applyMode(mode, true);
  window.cycleImmersiveThemeMode = cycleMode;

  function bindButtons(): void {
    const isDark = document.documentElement.classList.contains('dark');
    const body = document.body;
    if (body) {
      if (isDark) {
        body.classList.add('dark');
        body.classList.remove('light');
        body.setAttribute('data-theme-mode', 'dark');
      } else {
        body.classList.add('light');
        body.classList.remove('dark');
        body.setAttribute('data-theme-mode', 'light');
      }
    }
    updateSwitchUI(isDark);
    const toggles = document.querySelectorAll<HTMLElement>('[data-theme-toggle]');
    for (let i = 0; i < toggles.length; i++) {
      const btn = toggles[i];
      if (btn.getAttribute('data-theme-bound') === 'true') continue;
      btn.setAttribute('data-theme-bound', 'true');
      btn.dataset.themeToggleBound = 'true';
      btn.addEventListener('click', handleToggleClick);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindButtons);
  } else {
    bindButtons();
  }

  // OS system theme listener
  if (window.matchMedia) {
    try {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = function (): void {
        if (getStoredMode() === 'system') {
          applyMode('system');
        }
      };
      if (mq.addEventListener) {
        mq.addEventListener('change', handler);
      } else if ((mq as unknown as { addListener: (cb: () => void) => void }).addListener) {
        (mq as unknown as { addListener: (cb: () => void) => void }).addListener(handler);
      }
    } catch {}
  }

  // Native cross-tab storage sync (zero intra-window loop risk)
  window.addEventListener('storage', function (e: StorageEvent): void {
    if (e.key === STORAGE_KEY && e.newValue) {
      applyMode(e.newValue as 'light' | 'dark' | 'system');
    }
  });

  // Message listener for preview studio / iframes
  window.addEventListener('message', function (e: MessageEvent): void {
    if (!e.data || typeof e.data !== 'object') return;
    if ((e.data.action === 'toggleThemeMode' || e.data.type === 'toggleThemeMode') && e.data.mode) {
      applyMode(e.data.mode);
    }
  });
})();
